import {
  Button,
  ClickAwayListener,
  Divider,
  IconButton,
  InputBase,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { type RefObject, useMemo, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { WorkflowNodeType } from 'src/pages/Settings/workflows/utils/workflow-definition';
import { formatNodeTitle } from 'src/pages/Settings/workflows/utils/workflow-helpers';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';

const ToolButton = ({
  icon,
  label,
  shortcut,
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Tooltip title={shortcut ? `${label} (${shortcut})` : label}>
    <span>
      <IconButton size="small" aria-label={label} onClick={onClick} disabled={disabled}>
        <DynamicIcon name={icon} fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
);

interface FilterGroup {
  title: string;
  options: { value: string; label: string }[];
}

/**
 * The step filter: a pill that opens a grouped menu — Any step, then the kinds of step that decide
 * (Step type), then the steps that do something ("Do steps that…"), each named the way its card is,
 * and only the ones this workflow has.
 */
const StepFilter = () => {
  const { definition, catalog, view } = useWorkflowEditor();
  const { search, setSearch } = view;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const groups = useMemo<FilterGroup[]>(() => {
    const present = [...new Set(definition.nodes.map(({ type }) => type))].filter(
      (type) => type !== WorkflowNodeType.Trigger,
    );
    const option = (type: string) => ({ value: type, label: formatNodeTitle(type, catalog) });
    const byLabel = (a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label);
    return [
      {
        title: 'Step type',
        options: present.filter((type) => type === WorkflowNodeType.If).map(option),
      },
      {
        title: 'Do steps that…',
        options: present
          .filter((type) => type !== WorkflowNodeType.If)
          .map(option)
          .sort(byLabel),
      },
    ].filter((group) => group.options.length);
  }, [catalog, definition.nodes]);

  const selectedLabel =
    groups.flatMap((group) => group.options).find(({ value }) => value === search.stepType)
      ?.label ?? 'Any step';
  const pick = (stepType: string) => {
    setSearch({ ...search, stepType });
    setAnchor(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={(event) => setAnchor(event.currentTarget)}
        endIcon={<DynamicIcon name="expand_more" fontSize="small" />}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        sx={{ borderRadius: 999, textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {selectedLabel}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        // kept inside the pill, so a click in the menu isn't a click outside the search
        disablePortal
      >
        <MenuItem selected={!search.stepType} onClick={() => pick('')}>
          Any step
        </MenuItem>
        {groups.flatMap((group) => [
          <ListSubheader key={group.title} sx={{ lineHeight: '32px' }}>
            {group.title}
          </ListSubheader>,
          ...group.options.map(({ value, label }) => (
            <MenuItem
              key={value}
              selected={search.stepType === value}
              onClick={() => pick(value)}
            >
              {label}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};

/**
 * The tools over the top-left of the canvas, as in the Workflows prototype. Search is a round
 * button; open, it is a pill with "Find a step", the step filter, the match count, previous, next
 * and close — and a click outside it or Escape closes it and clears the search. Then Expand all,
 * Collapse all, Focus on the selected step, and Fit.
 */
export const WorkflowCanvasToolbar = ({
  searchOpen,
  onSearchOpenChange,
  searchInputRef,
  selectedId,
  onExpandAll,
  onCollapseAll,
  onFocusSelected,
  onFit,
}: {
  searchOpen: boolean;
  /** `false` closes the pill and clears the search. */
  onSearchOpenChange: (open: boolean) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  selectedId: string | null;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onFocusSelected: () => void;
  onFit: () => void;
}) => {
  const { view } = useWorkflowEditor();
  const { search, setSearch, matchIds, currentMatchIndex, stepMatch, searchActive } = view;

  return (
    <Stack direction="row" spacing={1} alignItems="center" className="nodrag nopan nowheel">
      {searchOpen ? (
        <ClickAwayListener onClickAway={() => onSearchOpenChange(false)}>
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: 1.5,
              pr: 0.5,
              py: 0.25,
              borderRadius: 999,
            }}
          >
            <DynamicIcon name="search" fontSize="small" sx={{ color: 'text.secondary' }} />
            <InputBase
              inputRef={searchInputRef}
              placeholder="Find a step"
              value={search.query}
              onChange={(event) => setSearch({ ...search, query: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && matchIds.length) {
                  event.preventDefault();
                  stepMatch(event.shiftKey ? -1 : 1);
                }
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  onSearchOpenChange(false);
                }
              }}
              inputProps={{ 'aria-label': 'Find a step' }}
              sx={{ width: 180, fontSize: 14 }}
            />
            <StepFilter />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
              aria-live="polite"
            >
              {searchActive
                ? `${matchIds.length ? currentMatchIndex + 1 : 0} / ${matchIds.length}`
                : ''}
            </Typography>
            <ToolButton
              icon="keyboard_arrow_up"
              label="Previous match"
              onClick={() => stepMatch(-1)}
              disabled={!matchIds.length}
            />
            <ToolButton
              icon="keyboard_arrow_down"
              label="Next match"
              onClick={() => stepMatch(1)}
              disabled={!matchIds.length}
            />
            <ToolButton
              icon="close"
              label="Close search"
              onClick={() => onSearchOpenChange(false)}
            />
          </Paper>
        </ClickAwayListener>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 999, display: 'flex' }}>
          <ToolButton
            icon="search"
            label="Search"
            shortcut="/"
            onClick={() => onSearchOpenChange(true)}
          />
        </Paper>
      )}
      <Paper
        variant="outlined"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.25, p: 0.25, borderRadius: 3 }}
      >
        <ToolButton icon="unfold_more" label="Expand all" shortcut="E" onClick={onExpandAll} />
        <ToolButton
          icon="unfold_less"
          label="Collapse all"
          shortcut="C"
          onClick={onCollapseAll}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <ToolButton
          icon="center_focus_strong"
          label={selectedId ? 'Focus on the selected step' : 'Select a step to focus on it'}
          onClick={onFocusSelected}
          disabled={!selectedId}
        />
        <ToolButton icon="fit_screen" label="Fit" shortcut="F" onClick={onFit} />
      </Paper>
    </Stack>
  );
};
